import React from "react";
import { CheckCircle2, XCircle, MessageCircle, Home, Clock, Instagram, PlayCircle, Info, Award, Users, Tag, TrendingUp } from "lucide-react";
import { Button } from "./ui/button";
import { Logo, VenueCards } from "./Shared";
import { LINE_URL, LAKEZE_IG, VALUE_PROPS } from "../mock";
import { track } from "../api";

export default function ResultPage({ status, onRestart }) {
  const qualified = status === "qualified";
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-border px-4 py-3">
        <div className="mx-auto max-w-2xl">
          <Logo className="h-7" />
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-4 py-8">
        <div className="fade-up text-center">
          {qualified ? (
            <>
              <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-orange-50">
                <CheckCircle2 className="h-12 w-12 text-brand" />
              </div>
              <h1 className="mt-4 text-2xl font-black sm:text-3xl">
                恭喜！你符合<span className="text-brand">人客多</span>初步合作資格
              </h1>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
                我們已將評估結果寄送至你的信箱，請查收。請加入下方 LINE@，
                將有專人於 <span className="font-bold text-foreground">72 小時內回覆</span>你；
                請留意 LINE 訊息，並記得將我們<span className="font-bold text-foreground">加為好友</span>，以免錯過通知。
              </p>

              <div className="mx-auto mt-6 max-w-md rounded-2xl border border-orange-200 brand-gradient p-5">
                <a href={LINE_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("line_click")}>
                  <Button className="h-12 w-full bg-brand text-base font-bold text-white hover:bg-[#EA580C]">
                    <MessageCircle className="mr-2 h-5 w-5" />
                    加入人客多客戶服務 LINE@
                  </Button>
                </a>
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-white/80 p-2.5 text-left text-xs text-[#9A3412]">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                  <span>
                    加入後，請主動告知我們：<span className="font-bold">「我是希望場域合作的業主」</span>，
                    專人將為你安排價值 $5,800 的線上策略諮詢與後續拍攝資源。
                  </span>
                </div>
                <p className="mt-2 flex items-center justify-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  加入後請留意訊息與好友邀請，專人將於 72 小時內回覆
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-gray-100">
                <XCircle className="h-12 w-12 text-gray-400" />
              </div>
              <h1 className="mt-4 text-2xl font-black sm:text-3xl">
                很遺憾，你目前不符合合作資格
              </h1>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
                根據你填寫的資訊，你的場域目前可能還不符合我們的合作條件。但這不代表沒有機會！建議你先參考以下成功案例，看看人客多如何幫助場域主人提升住房率和營收。
              </p>
            </>
          )}
        </div>

        {/* 人客多如何幫你賺錢（品牌定位） */}
        <div className="mt-10">
          <h2 className="mb-1 text-xl font-black">
            人客多如何幫你<span className="text-brand">賺錢</span>
          </h2>
          <p className="mb-4 text-sm text-muted-foreground">
            像 Marriott 一樣的品牌力，加上實戰行銷，讓你專注現場管理。
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {VALUE_PROPS.map((v, i) => {
              const Icon = [Award, Users, Tag, TrendingUp][i];
              return (
                <div
                  key={v.title}
                  className="flex gap-4 rounded-2xl border border-border bg-white p-5 shadow-sm transition hover:shadow-md"
                >
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-orange-50 text-brand">
                    <Icon className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="text-lg font-bold">{v.title}</p>
                    <p className="mt-1 text-base leading-relaxed text-muted-foreground">
                      {v.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 見證場域 */}
        <div className="mt-10">
          <h2 className="mb-1 text-xl font-black">見證場域</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            點擊卡片即可前往 FB / IG 了解更多，也可直接搜尋場域名稱。
          </p>
          <VenueCards />
        </div>

        {/* 合作網紅作品與影片 */}
        <div className="mt-10">
          <h2 className="mb-1 text-xl font-black">合作網紅 · 短影音作品</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            人客多與眾多網紅合作，累積 2280 萬觀看。點擊前往人客多官方 IG，直接看合作網紅拍攝的實際短影音。
          </p>
          <a href={LAKEZE_IG} target="_blank" rel="noopener noreferrer" className="block">
            <div className="group flex items-center gap-4 rounded-2xl border border-orange-200 brand-gradient p-4 transition hover:shadow-md">
              <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white text-brand shadow-sm">
                <Instagram className="h-7 w-7" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-bold">人客多 Lakeze 官方 IG</p>
                <p className="truncate text-sm text-muted-foreground">@lakezetw · 合作網紅短影音、場域實拍</p>
              </div>
              <span className="flex items-center gap-1 rounded-full bg-brand px-3 py-2 text-sm font-bold text-white">
                <PlayCircle className="h-4 w-4" /> 看影片
              </span>
            </div>
          </a>
        </div>

        <div className="mt-8 text-center">
          <Button variant="outline" onClick={onRestart} className="h-11 font-bold">
            <Home className="mr-2 h-4 w-4" /> 返回首頁
          </Button>
        </div>
      </div>

      <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} 人客多 Lakeze 旅宿集團
      </footer>
    </div>
  );
}
